import type maplibregl from "maplibre-gl";
import type { DataDrivenPropertyValueSpecification } from "maplibre-gl";

import { qldRvmCategoryColorExpression } from "./context-layer-meta";
import {
  CROWN_COVER_RASTER_MAX_ZOOM,
  CROWN_COVER_RASTER_MIN_ZOOM,
  CROWN_COVER_RASTER_TILES,
  DEFORESTATION_RISK_RASTER_MAX_ZOOM,
  DEFORESTATION_RISK_RASTER_MIN_ZOOM,
  DEFORESTATION_RISK_RASTER_TILES,
  FOREST_CHANGE_RASTER_MAX_ZOOM,
  FOREST_CHANGE_RASTER_MIN_ZOOM,
  FOREST_CHANGE_RASTER_TILES,
  FOREST_RASTER_MAX_ZOOM,
  FOREST_RASTER_MIN_ZOOM,
  FOREST_RASTER_TILES,
  IMAGERY_RASTER_MAX_ZOOM,
  IMAGERY_RASTER_MIN_ZOOM,
  IMAGERY_RASTER_TILES,
  LULC_RASTER_MAX_ZOOM,
  LULC_RASTER_MIN_ZOOM,
  LULC_RASTER_TILES,
  NVIS_RASTER_MAX_ZOOM,
  NVIS_RASTER_MIN_ZOOM,
  NVIS_RASTER_TILES,
  PMTILES_SOURCES,
  PMTILES_SOURCE_LAYERS,
} from "../config/deforestation";

function pmtilesUrl(absolutePath: string): string {
  return `pmtiles://${window.location.origin}${absolutePath}`;
}

function registerContextPmtilesLayer(
  m: maplibregl.Map,
  config: {
    sourceId: string;
    fillLayerId: string;
    lineLayerId: string;
    url: string;
    sourceLayer: string;
    fillColor: DataDrivenPropertyValueSpecification<string>;
    lineColor: DataDrivenPropertyValueSpecification<string>;
  },
): void {
  if (m.getSource(config.sourceId)) return;
  m.addSource(config.sourceId, {
    type: "vector",
    url: pmtilesUrl(config.url),
  });
  m.addLayer({
    id: config.fillLayerId,
    type: "fill",
    source: config.sourceId,
    "source-layer": config.sourceLayer,
    paint: { "fill-color": config.fillColor, "fill-opacity": 0 },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: config.lineLayerId,
    type: "line",
    source: config.sourceId,
    "source-layer": config.sourceLayer,
    paint: {
      "line-color": config.lineColor,
      "line-width": 1.2,
      "line-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

function registerContextPmtilesLayers(m: maplibregl.Map): void {
  registerContextPmtilesLayer(m, {
    sourceId: "context-capad",
    fillLayerId: "context-capad-fill",
    lineLayerId: "context-capad-line",
    url: PMTILES_SOURCES.capad,
    sourceLayer: PMTILES_SOURCE_LAYERS.capad,
    fillColor: "#16a34a",
    lineColor: "#15803d",
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-ramsar",
    fillLayerId: "context-ramsar-fill",
    lineLayerId: "context-ramsar-line",
    url: PMTILES_SOURCES.ramsar,
    sourceLayer: PMTILES_SOURCE_LAYERS.ramsar,
    fillColor: "#3b82f6",
    lineColor: "#1d4ed8",
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-world-heritage",
    fillLayerId: "context-world-heritage-fill",
    lineLayerId: "context-world-heritage-line",
    url: PMTILES_SOURCES.worldHeritage,
    sourceLayer: PMTILES_SOURCE_LAYERS.worldHeritage,
    fillColor: "#a855f7",
    lineColor: "#7c3aed",
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-national-heritage",
    fillLayerId: "context-national-heritage-fill",
    lineLayerId: "context-national-heritage-line",
    url: PMTILES_SOURCES.nationalHeritage,
    sourceLayer: PMTILES_SOURCE_LAYERS.nationalHeritage,
    fillColor: "#ec4899",
    lineColor: "#be185d",
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-commonwealth-heritage",
    fillLayerId: "context-commonwealth-heritage-fill",
    lineLayerId: "context-commonwealth-heritage-line",
    url: PMTILES_SOURCES.commonwealthHeritage,
    sourceLayer: PMTILES_SOURCE_LAYERS.commonwealthHeritage,
    fillColor: "#f59e0b",
    lineColor: "#b45309",
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-rfa",
    fillLayerId: "context-rfa-fill",
    lineLayerId: "context-rfa-line",
    url: PMTILES_SOURCES.regionalForestAgreements,
    sourceLayer: PMTILES_SOURCE_LAYERS.regionalForestAgreements,
    fillColor: "#0d9488",
    lineColor: "#115e59",
  });
  // Regulated vegetation is categorised by rvm_cat (A/B/C/R/X/Water);
  // each gets its own colour via a `match` expression so the legend
  // categories are distinguishable on the map. The flat colour swatch in
  // the left panel becomes a fallback (e.g. for features missing the
  // attribute).
  registerContextPmtilesLayer(m, {
    sourceId: "context-regulated-veg",
    fillLayerId: "context-regulated-veg-fill",
    lineLayerId: "context-regulated-veg-line",
    url: PMTILES_SOURCES.qldVegManagement,
    sourceLayer: PMTILES_SOURCE_LAYERS.qldVegManagement,
    fillColor: qldRvmCategoryColorExpression(),
    lineColor: qldRvmCategoryColorExpression(),
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-bushfire-history",
    fillLayerId: "context-bushfire-history-fill",
    lineLayerId: "context-bushfire-history-line",
    url: PMTILES_SOURCES.bushfireHistory,
    sourceLayer: PMTILES_SOURCE_LAYERS.bushfireHistory,
    fillColor: "#dc2626",
    lineColor: "#991b1b",
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-koala-habitat",
    fillLayerId: "context-koala-habitat-fill",
    lineLayerId: "context-koala-habitat-line",
    url: PMTILES_SOURCES.koalaHabitat,
    sourceLayer: PMTILES_SOURCE_LAYERS.koalaHabitat,
    fillColor: "#14b8a6",
    lineColor: "#0f766e",
  });
  registerContextPmtilesLayer(m, {
    sourceId: "context-critical-habitat",
    fillLayerId: "context-critical-habitat-fill",
    lineLayerId: "context-critical-habitat-line",
    url: PMTILES_SOURCES.criticalHabitat,
    sourceLayer: PMTILES_SOURCE_LAYERS.criticalHabitat,
    fillColor: "#ef4444",
    lineColor: "#b91c1c",
  });
}

// NVIS Level 2 Structural Formation — rendered as paletted WebP XYZ
// tiles at z3–z13
// The raster has its own per-class palette (16 L2 codes) baked into
// the WebP, so the layer only carries an opacity paint property — the
// frontend colour swatch in the legend is informational only.
function registerNvisRasterLayer(m: maplibregl.Map): void {
  const sourceId = "context-nvis-mvs";
  if (m.getSource(sourceId)) return;
  m.addSource(sourceId, {
    type: "raster",
    tiles: [NVIS_RASTER_TILES],
    tileSize: 256,
    minzoom: NVIS_RASTER_MIN_ZOOM,
    maxzoom: NVIS_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: `${sourceId}-raster`,
    type: "raster",
    source: sourceId,
    // Categorical raster — palette is baked into the PNG, so bilinear
    // resampling smears class boundaries between e.g. closed and open
    // forest. Force nearest-neighbour to keep edges crisp.
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

// Property boundaries — Geoscape land-parcel properties (aggregations of
// cadastral parcels). Rendered as a vector fill+line beneath the cadastre
// so individual parcel boundaries and labels stay on top.
function registerPropertyLayer(m: maplibregl.Map): void {
  if (m.getSource("geoscape-property")) return;
  m.addSource("geoscape-property", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.property),
  });
  m.addLayer({
    id: "geoscape-property-fill",
    type: "fill",
    source: "geoscape-property",
    "source-layer": PMTILES_SOURCE_LAYERS.property,
    paint: { "fill-color": "#0ea5e9", "fill-opacity": 0 },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "geoscape-property-line",
    type: "line",
    source: "geoscape-property",
    "source-layer": PMTILES_SOURCE_LAYERS.property,
    paint: {
      "line-color": "#0369a1",
      "line-width": 1.5,
      "line-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

function registerCadastreLayer(m: maplibregl.Map): void {
  if (m.getSource("geoscape-cadastre")) return;
  m.addSource("geoscape-cadastre", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.cadastre),
  });
  m.addLayer({
    id: "geoscape-cadastre-fill",
    type: "fill",
    source: "geoscape-cadastre",
    "source-layer": PMTILES_SOURCE_LAYERS.cadastre,
    paint: { "fill-color": "#a78bfa", "fill-opacity": 0 },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "geoscape-cadastre-line",
    type: "line",
    source: "geoscape-cadastre",
    "source-layer": PMTILES_SOURCE_LAYERS.cadastre,
    paint: {
      "line-color": "#7c3aed",
      "line-width": 1,
      "line-opacity": 0,
    },
    layout: { visibility: "none" },
  });
  // Demo portfolio outline. Filter starts as a no-match sentinel and
  // is replaced once the portfolio pids sidecar loads. Sits between the
  // cadastre fill/line and the user's selection so the selection ring is
  // still on top.
  m.addLayer({
    id: "portfolio-line",
    type: "line",
    source: "geoscape-cadastre",
    "source-layer": PMTILES_SOURCE_LAYERS.cadastre,
    paint: {
      "line-color": "#facc15",
      "line-width": 2.5,
      "line-opacity": 0,
    },
    filter: ["in", "cadastrePid", ""],
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "geoscape-cadastre-selected",
    type: "line",
    source: "geoscape-cadastre",
    "source-layer": PMTILES_SOURCE_LAYERS.cadastre,
    paint: {
      "line-color": "#facc15",
      "line-width": 2.5,
      "line-opacity": 0.9,
    },
    filter: ["in", "cadastrePid", ""],
    layout: { visibility: "visible" },
  });
  m.addLayer({
    id: "geoscape-cadastre-highlight",
    type: "line",
    source: "geoscape-cadastre",
    "source-layer": PMTILES_SOURCE_LAYERS.cadastre,
    paint: {
      "line-color": "#facc15",
      "line-width": 4,
      "line-opacity": 0,
    },
    filter: ["==", "cadastrePid", ""],
    layout: { visibility: "visible" },
  });
  m.addLayer({
    id: "geoscape-cadastre-labels",
    type: "symbol",
    source: "geoscape-cadastre",
    "source-layer": PMTILES_SOURCE_LAYERS.cadastre,
    minzoom: 14,
    layout: {
      "text-field": ["get", "parcelId"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 14, 9, 16, 11, 18, 13],
      "text-allow-overlap": false,
      "symbol-placement": "point",
      visibility: "visible",
    },
    paint: {
      "text-color": "#3f3f46",
      "text-halo-color": "rgba(255,255,255,0.85)",
      "text-halo-width": 1.2,
    },
  });
}

// Satellite imagery (2020 / 2024 / 2025 / latest) — hires WebP XYZ below the
// context layers so vector overlays sit on top.
function registerImageryLayers(m: maplibregl.Map): void {
  const windows = ["2020", "2024", "2025", "latest"] as const;
  for (const window of windows) {
    const sourceId = `imagery-${window}`;
    if (m.getSource(sourceId)) continue;
    m.addSource(sourceId, {
      type: "raster",
      tiles: [IMAGERY_RASTER_TILES[window]],
      tileSize: 256,
      minzoom: IMAGERY_RASTER_MIN_ZOOM,
      maxzoom: IMAGERY_RASTER_MAX_ZOOM,
    });
    m.addLayer({
      id: `${sourceId}-raster`,
      type: "raster",
      source: sourceId,
      paint: { "raster-opacity": 0 },
      layout: { visibility: "none" },
    });
  }
}

// SatSure LULC (2025 only for now) — 8-class paletted PNG XYZ
function registerLulcLayers(m: maplibregl.Map): void {
  const years = ["2025"] as const;
  for (const year of years) {
    const sourceId = `lulc-${year}`;
    if (m.getSource(sourceId)) continue;
    m.addSource(sourceId, {
      type: "raster",
      tiles: [LULC_RASTER_TILES[year]],
      tileSize: 256,
      minzoom: LULC_RASTER_MIN_ZOOM,
      maxzoom: LULC_RASTER_MAX_ZOOM,
    });
    m.addLayer({
      id: `${sourceId}-raster`,
      type: "raster",
      source: sourceId,
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }
}

// Forest crown-cover (2020 / 2025) — paletted PNG XYZ tiles
// coloured crown-cover classes overlay the satellite scene.
function registerForestLayers(m: maplibregl.Map): void {
  const years = ["2020", "2025"] as const;
  for (const year of years) {
    const sourceId = `forest-${year}`;
    if (m.getSource(sourceId)) continue;
    m.addSource(sourceId, {
      type: "raster",
      tiles: [FOREST_RASTER_TILES[year]],
      tileSize: 256,
      minzoom: FOREST_RASTER_MIN_ZOOM,
      maxzoom: FOREST_RASTER_MAX_ZOOM,
    });
    m.addLayer({
      id: `${sourceId}-raster`,
      type: "raster",
      source: sourceId,
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }
}

// SatSure Crown Cover (2020 / 2025) — paletted PNG XYZ tiles of the 5-class
// crown-cover classification (Closed / Open / Woodland / Unknown / Non-forest).
function registerCrownCoverLayers(m: maplibregl.Map): void {
  const years = ["2020", "2025"] as const;
  for (const year of years) {
    const sourceId = `crown-cover-${year}`;
    if (m.getSource(sourceId)) continue;
    m.addSource(sourceId, {
      type: "raster",
      tiles: [CROWN_COVER_RASTER_TILES[year]],
      tileSize: 256,
      minzoom: CROWN_COVER_RASTER_MIN_ZOOM,
      maxzoom: CROWN_COVER_RASTER_MAX_ZOOM,
    });
    m.addLayer({
      id: `${sourceId}-raster`,
      type: "raster",
      source: sourceId,
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }
}

// Forest change (2020↔2025) — paletted PNG XYZ tiles from
// pair into Tree Loss / Tree Gain / Density Reduction.
function registerForestChangeLayer(m: maplibregl.Map): void {
  const sourceId = "forest-change";
  if (m.getSource(sourceId)) return;
  m.addSource(sourceId, {
    type: "raster",
    tiles: [FOREST_CHANGE_RASTER_TILES],
    tileSize: 256,
    minzoom: FOREST_CHANGE_RASTER_MIN_ZOOM,
    maxzoom: FOREST_CHANGE_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: `${sourceId}-raster`,
    type: "raster",
    source: sourceId,
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

function registerDeforestationRiskLayer(m: maplibregl.Map): void {
  const sourceId = "deforestation-risk";
  if (m.getSource(sourceId)) return;
  m.addSource(sourceId, {
    type: "raster",
    tiles: [DEFORESTATION_RISK_RASTER_TILES],
    tileSize: 256,
    minzoom: DEFORESTATION_RISK_RASTER_MIN_ZOOM,
    maxzoom: DEFORESTATION_RISK_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: `${sourceId}-raster`,
    type: "raster",
    source: sourceId,
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

export function registerDeforestationLayers(m: maplibregl.Map): void {
  // Imagery first so vector overlays (cadastre + context) render on top.
  registerImageryLayers(m);

  // LULC sits between imagery and forest — broad land-use context
  // beneath the crown-cover overlay.
  registerLulcLayers(m);

  // Forest crown-cover sits directly above the imagery so the paletted
  // PNG overlays the satellite scene; vector overlays still render on
  // top of it via the registration order below.
  registerForestLayers(m);

  // Crown Cover sits directly above the AU Forest extent mask so the
  // SatSure 5-class classification overlays the binary forest layer.
  registerCrownCoverLayers(m);

  // Forest change sits above the per-year crown-cover layers so a user
  // who toggles change on top of either year sees the change overlay.
  registerForestChangeLayer(m);

  // NVIS raster sits between the satellite imagery and the vector
  // overlays — it carries landcover context that should be drawn
  // *under* parcel boundaries and the heritage/bushfire vector polygons.
  registerNvisRasterLayer(m);

  // Keep the risk mask above other rasters and below parcel and regulatory
  // vectors so that the risk pixels and boundaries remain readable.
  registerDeforestationRiskLayer(m);

  // Context layers (heritage, bushfire, koala, …) render between the
  // raster basemap and the cadastre, so parcel boundaries and labels
  // always sit on top — important because the cadastre is the demo's
  // primary click target.
  registerContextPmtilesLayers(m);

  // Property boundaries sit above the context layers but below the
  // cadastre, so individual parcels (the primary click target) and their
  // labels always render on top of the property aggregation outline.
  registerPropertyLayer(m);

  // Cadastre is the only Geoscape PMTiles archive — it carries
  // planning / LGA / property attribution
  registerCadastreLayer(m);
}
